import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { helper } from 'src/environments/helper';
import { GeneralService } from './general.service';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { NotificationService } from 'src/app/services/notification.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss']
})
export class GeneralComponent {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;
    isLoading = false;

    directiiActive: any[] = [];
    compartimenteActive!: Generic[];

    tari!: Generic[];
    judete!: Generic[];
    localitati!: Generic[];
    serii!: Generic[];
    statuses!: Generic[];
    priorities!: Generic[];
    localitatiExpeditor!: Generic[];

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredDirectii: any[] = [];
    directiiControl = new FormControl();

    filteredSerii: any[] = [];
    seriiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredTari: any[] = [];
    tariControl = new FormControl();

    filteredJudete: any[] = [];
    judeteControl = new FormControl();

    filteredJudeteExpeditor: any[] = [];
    judeteExpeditorControl = new FormControl();

    localitatiControl = new FormControl();
    isLocalitatiLoading = false;
    showLocalitatiMessage = true;
    localitatiLoadComplete = false;

    localitatiExpeditorControl = new FormControl();
    isLocalitatiExpeditorLoading = false;
    showLocalitatiExpeditorMessage = true;
    localitatiExpeditorLoadComplete = false;

    // Formulare pentru fiecare secțiune
    generalForm!: FormGroup;
    defaultForm!: FormGroup;
    registraturaForm!: FormGroup;
    awbForm!: FormGroup;
    expeditorForm!: FormGroup;
    adresaDefaultForm!: FormGroup;

    // Track modificări pentru fiecare secțiune
    generalModified = false;
    defaultModified = false;
    registraturaModified = false;
    awbModified = false;
    expeditorModified = false;
    adresaDefaultModified = false;

    // Valori inițiale pentru comparație
    private initialGeneralValues: any;
    private initialDefaultValues: any;
    private initialRegistraturaValues: any;
    private initialAwbValues: any;
    private initialExpeditorValues: any;
    private initialAdresaDefaultValues: any;

    private originalData: any[] = [];

    constructor(
        private fb: FormBuilder,
        private generalService: GeneralService,
        private commonService: CommonService,
        private notificationService: NotificationService,
        public permissionsService: PermissionsService
    ) { }

    ngOnInit(): void {
        this.loadDirectii();
        this.loadTari();
        this.initializeForms();
        this.loadData();
        this.renderFieldsSearch();
        this.loadSerii();
        this.loadStatuses();
        this.loadPriorities();

        if (!this.permissionsService.hasPermission('indoc-module.settings.general-settings.update')) {
            this.disableAllForms();
        }
    }

    private disableAllForms(): void {
        this.generalForm.disable();
        this.defaultForm.disable();
        this.registraturaForm.disable();
        this.awbForm.disable();
        this.expeditorForm.disable();
        this.adresaDefaultForm.disable();

        this.directiiControl.disable();
        this.seriiControl.disable();
        this.compartimenteControl.disable();
        this.tariControl.disable();
        this.judeteControl.disable();
        this.judeteExpeditorControl.disable();
        this.localitatiControl.disable();
        this.localitatiExpeditorControl.disable();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.seriiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSerii = this.serii;
                    return of(this.serii);
                }
                return this.filterSerii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredSerii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.tariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTari = this.tari;
                    return of(this.tari);
                }
                return this.filterTari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.judeteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredJudete = this.judete;
                    return of(this.judete);
                }
                return this.filterJudete(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredJudete = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.localitatiControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(600)
            )
            .subscribe(localitate => {
                this.fetchLocalitati(localitate);
                this.showLocalitatiMessage = true;
                this.localitatiLoadComplete = false;
            });

        this.judeteExpeditorControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredJudeteExpeditor = this.judete;
                    return of(this.judete);
                }
                return this.filterJudete(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredJudeteExpeditor = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.localitatiExpeditorControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(600)
            )
            .subscribe(localitate => {
                this.fetchLocalitatiExpeditor(localitate);
                this.showLocalitatiExpeditorMessage = true;
                this.localitatiExpeditorLoadComplete = false;
            });
    }

    private initializeForms(): void {
        // Setări Generale
        this.generalForm = this.fb.group({
            institutie_selectata: [''],
            institutie_denumire: [''],
            email_comunicare: [''],
            institutie_website: ['']
        });

        // Setări Default
        this.defaultForm = this.fb.group({
            default_status: [''],
            default_priority: [''],
        });

        // Setări Registratura
        this.registraturaForm = this.fb.group({
            registru_oficial: [''],
            asteptare_semnaturi_status: [''],
            de_inregistrat_srp_status: [''],
            documents_to_close_status: [''],
            registered_status: [''],
            serviciu_registratura: [''],
        });

        // Setări AWB
        this.awbForm = this.fb.group({
            BORDEROU_ACTIV: [''],
            cod_trimitere_awb: [''],
            oficiu_postal: [''],
            greutate: [''],
            status_posta: [''],
            status_indoc: ['']
        });

        // Expeditor
        this.expeditorForm = this.fb.group({
            expeditor_nume: [''],
            expeditor_persoana_contact: [''],
            expeditor_judet: [''],
            expeditor_localitate: [''],
            expeditor_adresa: [''],
            expeditor_cod_postal: [''],
            expeditor_telefon: [''],
            expeditor_email: ['']
        });

        // Adresă Default
        this.adresaDefaultForm = this.fb.group({
            default_adresa_tara: [''],
            default_adresa_judet: [''],
            default_adresa_localitate: ['']
        });

        //Subscrie la modificări
        this.setupValueChangeListeners();
    }

    private setupValueChangeListeners(): void {
        this.generalForm.valueChanges.subscribe(() => {
            this.generalModified = this.hasFormChanged(this.generalForm, this.initialGeneralValues);
        });

        this.defaultForm.valueChanges.subscribe(() => {
            this.defaultModified = this.hasFormChanged(this.defaultForm, this.initialDefaultValues);
        });

        this.registraturaForm.valueChanges.subscribe(() => {
            this.registraturaModified = this.hasFormChanged(this.registraturaForm, this.initialRegistraturaValues);
        });

        this.awbForm.valueChanges.subscribe(() => {
            this.awbModified = this.hasFormChanged(this.awbForm, this.initialAwbValues);
        });

        this.expeditorForm.valueChanges.subscribe(() => {
            this.expeditorModified = this.hasFormChanged(this.expeditorForm, this.initialExpeditorValues);
        });

        this.adresaDefaultForm.valueChanges.subscribe(() => {
            this.adresaDefaultModified = this.hasFormChanged(this.adresaDefaultForm, this.initialAdresaDefaultValues);
        });
    }

    private hasFormChanged(form: FormGroup, initialValues: any): boolean {
        if (!initialValues) return false;
        return JSON.stringify(form.value) !== JSON.stringify(initialValues);
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadDirectii() {
        this.isLoading = true;
        this.commonService.getDirectiiActive()
            .pipe(
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (res: any) => {
                    this.directiiActive = res;
                    this.filteredDirectii = [...this.directiiActive];
                    return;
                },
                error: (err: any) => {
                    console.log(err.error);
                }
            })
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.generalForm.get('institutie_selectata')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.registraturaForm.controls['serviciu_registratura'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.registraturaForm.get('serviciu_registratura')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    private loadData(): void {
        this.isLoading = true;
        this.generalService.list().subscribe({
            next: (response: any) => {
                if (response.errors === false && response.data) {
                    this.populateForms(response.data);
                    this.originalData = response.data;
                }
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading data:', error);
                this.isLoading = false;
            }
        });
    }

    private populateForms(data: any[]): void {
        const dataMap = this.createDataMap(data);

        // Populare Setări Generale
        const generalValues = {
            institutie_selectata: dataMap['institutie_selectata'] ? parseInt(dataMap['institutie_selectata']) : '',
            institutie_denumire: dataMap['institutie_denumire'] || '',
            email_comunicare: dataMap['email_comunicare'] || '',
            institutie_website: dataMap['institutie_website'] || ''
        };
        this.generalForm.patchValue(generalValues);
        this.initialGeneralValues = { ...generalValues };
        if (this.generalForm.get('institutie_selectata')?.value) {
            this.loadCompartimenteActive(this.generalForm.get('institutie_selectata')?.value);
        }

        // Populare Setări Default
        const defaultValues = {
            default_status: dataMap['default_status'] ? parseInt(dataMap['default_status']) : '',
            default_priority: dataMap['default_priority'] ? parseInt(dataMap['default_priority']) : '',
        };
        this.defaultForm.patchValue(defaultValues);
        this.initialDefaultValues = { ...defaultValues };

        // Populare Setări Registratura
        const registraturaValues = {
            registru_oficial: dataMap['registru_oficial'] ? parseInt(dataMap['registru_oficial']) : '',
            asteptare_semnaturi_status: dataMap['asteptare_semnaturi_status'] ? parseInt(dataMap['asteptare_semnaturi_status']) : '',
            de_inregistrat_srp_status: dataMap['de_inregistrat_srp_status'] ? parseInt(dataMap['de_inregistrat_srp_status']) : '',
            documents_to_close_status: dataMap['documents_to_close_status'] ? parseInt(dataMap['documents_to_close_status']) : '',
            registered_status: dataMap['registered_status'] ? parseInt(dataMap['registered_status']) : '',
            serviciu_registratura: dataMap['serviciu_registratura'] ? parseInt(dataMap['serviciu_registratura']) : '',
        };
        this.registraturaForm.patchValue(registraturaValues);
        this.initialRegistraturaValues = { ...registraturaValues };

        // Populare Setări AWB
        const awbValues = {
            BORDEROU_ACTIV: dataMap['BORDEROU_ACTIV'] || '',
            cod_trimitere_awb: dataMap['cod_trimitere_awb'] || '',
            oficiu_postal: dataMap['oficiu_postal'] || '',
            greutate: dataMap['greutate'] || '',
            status_posta: dataMap['status_posta'] ? parseInt(dataMap['status_posta']) : '',
            status_indoc: dataMap['status_indoc'] ? parseInt(dataMap['status_indoc']) : ''
        };
        this.awbForm.patchValue(awbValues);
        this.initialAwbValues = { ...awbValues };

        // Populare Expeditor
        const expeditorValues = {
            expeditor_nume: dataMap['expeditor_nume'] || '',
            expeditor_persoana_contact: dataMap['expeditor_persoana_contact'] || '',
            expeditor_judet: dataMap['expeditor_judet'] ? parseInt(dataMap['expeditor_judet']) : '',
            expeditor_localitate: dataMap['expeditor_localitate'] ? parseInt(dataMap['expeditor_localitate']) : '',
            expeditor_adresa: dataMap['expeditor_adresa'] || '',
            expeditor_cod_postal: dataMap['expeditor_cod_postal'] || '',
            expeditor_telefon: dataMap['expeditor_telefon'] || '',
            expeditor_email: dataMap['expeditor_email'] || ''
        };
        this.expeditorForm.patchValue(expeditorValues);
        this.initialExpeditorValues = { ...expeditorValues };

        // Populare Adresă Default
        const adresaDefaultValues = {
            default_adresa_tara: dataMap['default_adresa_tara'] ? parseInt(dataMap['default_adresa_tara']) : '',
            default_adresa_judet: dataMap['default_adresa_judet'] ? parseInt(dataMap['default_adresa_judet']) : '',
            default_adresa_localitate: dataMap['default_adresa_localitate'] ? parseInt(dataMap['default_adresa_localitate']) : ''
        };
        this.adresaDefaultForm.patchValue(adresaDefaultValues);
        this.initialAdresaDefaultValues = { ...adresaDefaultValues };

        const localitateNumeDenumire = dataMap['default_adresa_localitate_label'] || '';
        if (localitateNumeDenumire) {
            this.localitatiControl.patchValue(localitateNumeDenumire);
            this.localitatiLoadComplete = true;
            this.showLocalitatiMessage = false;
        }

        const localitateExpeditorNumeDenumire = dataMap['expeditor_localitate_label'] || '';
        if (localitateNumeDenumire) {
            this.localitatiExpeditorControl.patchValue(localitateExpeditorNumeDenumire);
            this.localitatiExpeditorLoadComplete = true;
            this.showLocalitatiExpeditorMessage = false;
        }
    }

    private createDataMap(data: any[]): { [key: string]: string } {
        const map: { [key: string]: string } = {};
        data.forEach(item => {
            map[item.name] = item.value;
            if (item.label) {
                map[item.name + '_label'] = item.label;
            }
        });
        return map;
    }

    updateGeneral(): void {
        if (this.generalForm.valid) {
            const changedFields = this.getChangedFields(this.generalForm.value, this.initialGeneralValues);
            const updates = this.createUpdatePayloads(changedFields);
            this.updateMultipleSettings(updates, 'general');
        }
    }

    updateDefault(): void {
        if (this.defaultForm.valid) {
            const changedFields = this.getChangedFields(this.defaultForm.value, this.initialDefaultValues);
            const updates = this.createUpdatePayloads(changedFields);
            this.updateMultipleSettings(updates, 'default');
        }
    }

    updateRegistratura(): void {
        if (this.registraturaForm.valid) {
            const changedFields = this.getChangedFields(this.registraturaForm.value, this.initialRegistraturaValues);
            const updates = this.createUpdatePayloads(changedFields);
            this.updateMultipleSettings(updates, 'registratura');
        }
    }

    updateAwb(): void {
        if (this.awbForm.valid) {
            const changedFields = this.getChangedFields(this.awbForm.value, this.initialAwbValues);
            const updates = this.createUpdatePayloads(changedFields);
            this.updateMultipleSettings(updates, 'awb');
        }
    }

    updateExpeditor(): void {
        if (this.expeditorForm.valid) {
            const payload = this.createComplexUpdatePayload(this.expeditorForm.value, 'expeditor');
            this.updateComplexSettings(payload, 'expeditor');
        }
    }

    updateAdresaDefault(): void {
        if (this.adresaDefaultForm.valid) {
            const payload = this.createComplexUpdatePayload(this.adresaDefaultForm.value, 'adresa');
            this.updateComplexSettings(payload, 'adresa');
        }
    }

    private getChangedFields(currentValues: any, initialValues: any): { [key: string]: any } {
        const changed: { [key: string]: any } = {};
        Object.keys(currentValues).forEach(key => {
            if (currentValues[key] !== initialValues[key]) {
                changed[key] = currentValues[key];
            }
        });
        return changed;
    }

    private createUpdatePayloads(changedFields: { [key: string]: any }): any[] {
        const payloads: any[] = [];

        Object.keys(changedFields).forEach(fieldName => {
            const originalItem = this.originalData.find(item => item.name === fieldName);
            if (originalItem) {
                payloads.push({
                    id: originalItem.id,
                    name: originalItem.name,
                    value: changedFields[fieldName]
                });
            }
        });

        return payloads;
    }

    private createComplexUpdatePayload(formValues: any, section: string): any {
        const payload: any = {};

        Object.keys(formValues).forEach(fieldName => {
            const originalItem = this.originalData.find(item => item.name === fieldName);
            if (originalItem) {
                payload[fieldName] = {
                    id: originalItem.id,
                    name: originalItem.name,
                    value: formValues[fieldName],
                    denumire: this.getDenumireForField(fieldName, formValues[fieldName])
                };
            }
        });

        return payload;
    }

    private getDenumireForField(fieldName: string, value: any): string {
        // Pentru câmpurile de adresă default
        if (fieldName === 'default_adresa_tara') {
            const tara = this.tari?.find(t => t.id === value);
            return tara ? tara.name : '';
        }
        if (fieldName === 'default_adresa_judet') {
            const judet = this.judete?.find(j => j.id === value);
            return judet ? judet.name : '';
        }
        if (fieldName === 'default_adresa_localitate') {
            const localitate = this.localitati?.find(l => l.id === value);
            return localitate ? localitate.name : '';
        }
        if (fieldName === 'expeditor_judet') {
            const judet = this.judete?.find(j => j.id === value);
            return judet ? judet.name : '';
        }
        if (fieldName === 'expeditor_localitate') {
            const localitate = this.localitatiExpeditor?.find(l => l.id === value);
            return localitate ? localitate.name : '';
        }

        // Pentru toate celelalte câmpuri (expeditor), denumirea este aceeași cu value-ul
        return value || '';
    }

    private updateMultipleSettings(payloads: any[], section: string): void {
        if (payloads.length === 0) {
            return;
        }

        this.isLoading = true;
        let completedRequests = 0;
        let hasError = false;

        payloads.forEach(payload => {
            this.generalService.update(payload).subscribe({
                next: (response: any) => {
                    completedRequests++;

                    if (response.errors !== false) {
                        hasError = true;
                    }

                    // Check if all requests completed
                    if (completedRequests === payloads.length) {
                        if (!hasError) {
                            // Success - reload data
                            this.loadData();
                            // Reset modified flag
                            this.resetModifiedFlag(section);
                        }
                        this.isLoading = false;
                    }
                },
                error: (err) => {
                    console.error('Error updating settings:', err);
                    let errorMessage;

                    if (!hasError) {
                        try {
                            errorMessage = err.error.errors.message[0];
                        } catch (error) {
                            errorMessage = helper.dialogConfig.generalMessages.error;
                        }
                    }

                    completedRequests++;
                    hasError = true;

                    if (completedRequests === payloads.length) {
                        this.isLoading = false;
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            errorMessage,
                            this.errorIcon
                        );
                    }
                }
            });
        });
    }

    private updateComplexSettings(payload: any, section: string): void {
        this.isLoading = true;
        this.generalService.updateMultiple(payload).subscribe({
            next: (response: any) => {
                if (response.errors === false) {
                    // Success - reload data
                    this.loadData();
                    // Reset modified flag
                    this.resetModifiedFlag(section);
                }
                this.isLoading = false;
            },
            error: (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.notificationService.warningSwal(
                    this.errorTitle,
                    msg,
                    this.errorIcon
                );
                this.isLoading = false;
            }
        });
    }

    private resetModifiedFlag(section: string): void {
        switch (section) {
            case 'general':
                this.generalModified = false;
                this.initialGeneralValues = { ...this.generalForm.value };
                break;
            case 'default':
                this.defaultModified = false;
                this.initialDefaultValues = { ...this.defaultForm.value };
                break;
            case 'registratura':
                this.registraturaModified = false;
                this.initialRegistraturaValues = { ...this.registraturaForm.value };
                break;
            case 'awb':
                this.awbModified = false;
                this.initialAwbValues = { ...this.awbForm.value };
                break;
            case 'expeditor':
                this.expeditorModified = false;
                this.initialExpeditorValues = { ...this.expeditorForm.value };
                break;
            case 'adresa':
                this.adresaDefaultModified = false;
                this.initialAdresaDefaultValues = { ...this.adresaDefaultForm.value };
                break;
        }
    }

    loadTari() {
        this.isLoading = true;
        this.commonService.getTariActive()
            .pipe(
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.tari = results;
                    this.filteredTari = [...this.tari];
                    this.adresaDefaultForm.get('default_adresa_tara')?.patchValue(1);
                    this.loadJudete(this.adresaDefaultForm.get('default_adresa_tara')?.value);
                },
                error: (error) => {
                    console.error('Error in get tari: ', error);
                }
            })
    }

    filterTari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tari.filter(tara =>
            this.normalizeString(tara.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onTaraSelectClosed() {
        const selectedValue = this.tariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTari.some(tara =>
                tara.id === this.adresaDefaultForm.get('default_adresa_tara')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tariControl.setValue(null);
                this.filteredTari = this.tari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onTaraChange(taraId: number): void {
        this.adresaDefaultForm.controls['default_adresa_judet'].patchValue(-1);
        this.adresaDefaultForm.controls['default_adresa_localitate'].patchValue(-1);

        if (taraId == -1) {
            this.judete = [];
            this.filteredJudete = [];

            this.localitati = [];
            return
        }

        if (taraId) {
            this.loadJudete(taraId);
        }
    }

    loadJudete(taraId: number) {
        this.isLoading = true;

        let judeteObservable$ = taraId
            ? this.commonService.getJudeteActive(taraId)
            : this.commonService.getJudeteActive();

        judeteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (judete) => {
                this.judete = [...judete];
                this.filteredJudete = [...judete];
                this.filteredJudeteExpeditor = [...judete];
            },
            error: (err) => {
                console.log(err);
                this.filteredJudete = [];
                this.filteredJudeteExpeditor = [];
            }
        });
    }

    onJudetChange(judetId: number) {
        this.adresaDefaultForm.controls['default_adresa_localitate'].patchValue('');
        this.localitatiControl.patchValue('');
        this.localitati = [];
        this.showLocalitatiMessage = true;
        this.localitatiLoadComplete = false;
    }

    onJudeteSelectClosed() {
        const selectedValue = this.judeteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredJudete.some(judet =>
                judet.id === this.adresaDefaultForm.get('default_adresa_judet')?.value
            );

            if (!isSelectedInFilteredList) {
                this.judeteControl.setValue(null);
                this.filteredJudete = this.judete;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterJudete(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.judete.filter(judet =>
            this.normalizeString(judet.name).includes(normalizedSearchTerm)
        ));
    }

    fetchLocalitati(localitate: string) {
        let idCountry = this.adresaDefaultForm.get('default_adresa_tara')?.value;
        let idJudet = this.adresaDefaultForm.get('default_adresa_judet')?.value;

        let country = (idCountry && idCountry !== -1) ? idCountry : undefined;
        let judet = (idJudet && idJudet !== -1) ? idJudet : undefined;

        if (localitate && localitate.length >= 2) {
            this.isLocalitatiLoading = true;
            this.commonService.getLocalitatiActive(country, judet, localitate).subscribe({
                next: (response: any) => {
                    this.localitati = response;
                    this.isLocalitatiLoading = false;
                    this.localitatiLoadComplete = true;
                    this.showLocalitatiMessage = this.localitati.length === 0;
                },
                error: (err: any) => {
                    this.isLocalitatiLoading = false;
                    this.showLocalitatiMessage = true;
                    this.localitatiLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.localitati = [];
            this.showLocalitatiMessage = true;
            this.localitatiLoadComplete = false;
        }
    }

    onJudetExpeditorChange(judetId: number) {
        this.expeditorForm.controls['expeditor_localitate'].patchValue('');
        this.localitatiExpeditorControl.patchValue('');
        this.localitatiExpeditor = [];
        this.showLocalitatiExpeditorMessage = true;
        this.localitatiExpeditorLoadComplete = false;
    }

    onJudeteExpeditorSelectClosed() {
        const selectedValue = this.judeteExpeditorControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredJudeteExpeditor.some(judet =>
                judet.id === this.expeditorForm.get('expeditor_judet')?.value
            );

            if (!isSelectedInFilteredList) {
                this.judeteExpeditorControl.setValue(null);
                this.filteredJudeteExpeditor = this.judete;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    fetchLocalitatiExpeditor(localitate: string) {
        let idCountry = 1;
        let idJudet = this.expeditorForm.get('expeditor_judet')?.value;

        let country = (idCountry && idCountry !== -1) ? idCountry : undefined;
        let judet = (idJudet && idJudet !== -1) ? idJudet : undefined;

        if (localitate && localitate.length >= 2) {
            this.isLocalitatiExpeditorLoading = true;
            this.commonService.getLocalitatiActive(country, judet, localitate).subscribe({
                next: (response: any) => {
                    this.localitatiExpeditor = response;
                    this.isLocalitatiExpeditorLoading = false;
                    this.localitatiExpeditorLoadComplete = true;
                    this.showLocalitatiExpeditorMessage = this.localitatiExpeditor.length === 0;
                },
                error: (err: any) => {
                    this.isLocalitatiExpeditorLoading = false;
                    this.showLocalitatiExpeditorMessage = true;
                    this.localitatiExpeditorLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.localitatiExpeditor = [];
            this.showLocalitatiExpeditorMessage = true;
            this.localitatiExpeditorLoadComplete = false;
        }
    }

    loadSerii() {
        this.isLoading = true;
        this.commonService.getSeriiActive()
            .pipe(
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.serii = results;
                    this.filteredSerii = [...this.serii];
                },
                error: (error) => {
                    console.error('Error in get serii: ', error);
                }
            })
    }

    filterSerii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.serii.filter(serie =>
            this.normalizeString(serie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onSerieSelectClosed() {
        const selectedValue = this.seriiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredSerii.some(serie =>
                serie.id === this.registraturaForm.get('registru_oficial')?.value
            );

            if (!isSelectedInFilteredList) {
                this.seriiControl.setValue(null);
                this.filteredSerii = this.serii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadStatuses() {
        this.isLoading = true;
        this.commonService.getStatusesActive()
            .pipe(
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.statuses = results;
                },
                error: (error) => {
                    console.error('Error in get statuses: ', error);
                }
            })
    }

    loadPriorities() {
        this.isLoading = true;
        this.commonService.getPrioritiesActive()
            .pipe(
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.priorities = results;
                },
                error: (error) => {
                    console.error('Error in get priorities: ', error);
                }
            })
    }

    clearCache() {
        Swal.fire({
            title: helper.swalTextConfig.commonTitles.areYouSure,
            text: helper.swalTextConfig.generalMessages.cleanCache,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: helper.swalTextConfig.buttonLabels.confirm,
            cancelButtonText: helper.swalTextConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.generalService.clearCache().subscribe(() => {
                    Swal.fire({
                        title: helper.swalTextConfig.headers.deleted,
                        text: helper.swalTextConfig.generalMessages.cleanCacheSuccess,
                        icon: 'success',
                        confirmButtonText: helper.swalTextConfig.buttonLabels.ok,
                    });
                });
            }
        });
    }
}